<template>

  <div>
    <Modal
      :title="$t('titles.discounts_spothero')"
      :submit="submit"
      :submitting="submitting"
    >

      <div class="d-flex mb-4">
        <div><span class="text-grey">Channel:</span> <strong>{{ ChannelRegistry.findTitle(channelFacility.channel) }}</strong></div>
        <div class="ms-3"><span class="text-grey">OCRA CF ID:</span> <strong>{{ channelFacility.ID }}</strong></div>
        <div class="ms-3"><span class="text-grey">Channel-Side Facility:</span> <strong>{{ channelFacility.externalFacilityID }}</strong></div>
      </div>

      <v-alert type="info" icon="$info" class="mb-6" variant="tonal">
        SpotHero applies the <strong>single highest applicable discount</strong> per booking — tiers do not stack.
      </v-alert>

      <flex-discount-group
        v-model="v$.multiDay.$model"
        :title="$t('titles.multi_day_discounts')"
        :chip="$t('titles.multi_day')"
        chip-color="success"
        :help="'Applies to stays > 24 hrs'"
        :empty-message="'No multiday discounts configured.'"
        :days-prefix="'Stay duration'"
        @discount-deleted="discountId => deletedIds.push(discountId)"
        class="mb-6"
      />

      <flex-discount-group
        v-model="v$.earlyBooking.$model"
        :title="$t('titles.early_booking_discounts')"
        :chip="$t('titles.early_booking')"
        chip-color="info"
        :help="'Applies when booked ≥ 24 hrs in advance'"
        :empty-message="'No early booking discounts configured.'"
        :days-prefix="'Booked'"
        :days-suffix="'in advance'"
        @discount-deleted="discountId => deletedIds.push(discountId)"
      />

      <template v-slot:actions>

        <v-btn prepend-icon="$delete" @click="clearAll()" :disabled="!v$.multiDay.$model.length && !v$.earlyBooking.$model.length">
          {{ $t('actions.clear_all_discounts') }}
        </v-btn>

      </template>

    </Modal>
  </div>

</template>

<script setup lang="ts">

import { onBeforeMount, reactive, ref } from 'vue'

// components
import Modal from '../../../Modal.vue'
import FlexDiscountGroup from './FlexDiscountGroup.vue'

import { ChannelFacility } from '../../../../models/ChannelFacility'
import SpotHeroFlexDiscount from '../../../../models/SpotHeroFlexDiscount'
import type { PatchSpotHeroFlexDiscountsRequest } from '../../../../models/SpotHeroFlexDiscount'

// misc
// @ts-ignore
import { closeDialog } from 'vue3-promise-dialog'
import ChannelRegistry from '../../../../channels/ChannelRegistry'
import type { FlexDiscountRow } from './types'
import useVuelidate from '@vuelidate/core'
import NotificationService from '../../../../services/NotificationService'

/**
 * Props
 */

interface Props {
  locationId: number
  locationProductId: number
  channelFacility: ChannelFacility
  spotheroFlexDiscounts: SpotHeroFlexDiscount[]
}

const props = defineProps<Props>()

/**
 * Form state and rules
 */

onBeforeMount(() => {

  // populate formState

  props.spotheroFlexDiscounts.toSorted((sfd1, sfd2) => sfd1.days - sfd2.days).forEach(sfd => {

    const row: FlexDiscountRow = {
      id: sfd.ID,
      days: sfd.days,
      percentage: sfd.percentage
    }

    if (sfd.discountType === 'multi_day') {
      formState.multiDay.push(row)
    } else if (sfd.discountType === 'early_booking') {
      formState.earlyBooking.push(row)
    }

  })

})

interface FormState {
  multiDay: FlexDiscountRow[]
  earlyBooking: FlexDiscountRow[]
}

const formState = reactive<FormState>({
  multiDay: [],
  earlyBooking: [],
})

const v$ = useVuelidate({
  multiDay: {},
  earlyBooking: {},
}, formState)

/**
 *
 */

function clearAll() {

  formState.multiDay.filter(row => !!row.id).forEach(row => {
    deletedIds.value.push(row.id!)
  })

  formState.earlyBooking.filter(row => !!row.id).forEach(row => {
    deletedIds.value.push(row.id!)
  })

  formState.multiDay.length = 0
  formState.earlyBooking.length = 0
}

/**
 * Submission
 */

const submitting = ref<boolean>(false)
const deletedIds = ref<number[]>([])

async function submit() {

  const isValid = await v$.value.$validate()

  if (!isValid) {
    return
  }

  submitting.value = true

  const payload: PatchSpotHeroFlexDiscountsRequest = {
    creates: [],
    updates: [],
    deletes: deletedIds.value,
  }

  patchPayload(payload, formState.multiDay, 'multi_day')
  patchPayload(payload, formState.earlyBooking, 'early_booking')

  let promise

  if (payload.creates.length || payload.updates.length || payload.deletes.length) {
    promise = SpotHeroFlexDiscount.patch(
      props.locationId,
      props.locationProductId,
      props.channelFacility.ID,
      payload
    )
  } else {
    promise = Promise.resolve()
  }

  promise.catch(e => {
    console.error(e)
    NotificationService.error("Couldn\'t update discounts!")
  }).then(() => {
    NotificationService.success("Discounts updated")
    closeDialog(true)
  }).finally(() => {
    submitting.value = false
  })

}

function patchPayload(payload: PatchSpotHeroFlexDiscountsRequest, rows: FlexDiscountRow[], discountType: string) {

  rows.forEach(row => {

    if (row.id) {
      payload.updates.push({
        ID: row.id!,
        days: row.days!,
        percentage: row.percentage!,
      })
    } else {
      payload.creates.push({
        discountType: discountType,
        days: row.days!,
        percentage: row.percentage!,
      })
    }

  })

}

</script>
